import { Outlet, useLocation } from "react-router-dom";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import TablePagination from "@mui/material/TablePagination";
import { flexRender } from "@tanstack/react-table";
import {
  AppTable,
  AppTableBody,
  AppTableCell,
  AppTableContainer,
  AppTableHead,
  AppTableRow,
  AppTableSort,
} from "../../../../../../components/common/app-table/ui";
import useDefaultTable from "../../../../../../components/common/app-table/useDefaultTable";
import React, { useEffect, useState } from "react";
import { ESREquipmentServiceColumns } from "./utils/types";
import { Button, Typography } from "@mui/material";
import { useCreateESREquipmentServicesColumns } from "../hooks/useCreateESREquipmentServicesColumns";
import { useESRApprovalContext } from "../../Context/ESRApprovalContext";
import { Add } from "@mui/icons-material";
import DeleteConfirmDialog from "../DeleteConfirmDialog";
import { useUpdateESR } from "../../../../../../hooks/useESRApproval";

const ESREquipmentServicesTable = () => {
  const location = useLocation();
  const updateESR = useUpdateESR();
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const [isDeletionConfirmed, setIsDeletionConfirmed] =
    useState<boolean>(false);
  const {
    setOpenESDialog,
    setESActionType,
    esrActionType,
    esrEquipmentServicesArray,
    setEsrEquipmentServicesArray,
    currentESSelected,
    setCurrentESSelected,
    setOpenEmailSendingDialog,
    openESDeleteDialog,
    setOpenESDeleteDialog,
  } = useESRApprovalContext();

  const esrEquipmentServicesColumns = useCreateESREquipmentServicesColumns();

  const { table, dataCount, pageIndex, pageSize } =
    useDefaultTable<ESREquipmentServiceColumns>(
      esrEquipmentServicesArray,
      esrEquipmentServicesColumns
    );

  const goToAddES = () => {
    setESActionType("new");
    setCurrentESSelected({
      id: "",
      taskItem: "",
      taskTypeId: "",
      taskTypeItem: "",
      partNumber: "",
      quantity: "0",
      remarks: "",
    });
    setOpenESDialog(true);
  };

  useEffect(() => {
    if (isDeletionConfirmed) {
      const newArray = esrEquipmentServicesArray.filter(
        (sr, index) => String(index) != currentESSelected.id
      );
      setEsrEquipmentServicesArray(newArray);
      setIsDeletionConfirmed(false);
    }
  }, [isDeletionConfirmed]);

  return (
    <>
      <Stack direction="column" mb={2} mt={5} gap={3}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h6" component="div">
            Task & Part Number List
          </Typography>

          <Box>
            {esrActionType != "viewing" &&
            pathSegments[2] != "create" &&
            pathSegments[0] == "esr-management" ? (
              <Button
                onClick={() => setOpenEmailSendingDialog(true)}
                disabled={updateESR.isLoading || updateESR.isSuccess}
                disableElevation
                variant="contained"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: 1.5,
                  marginRight: "1em",
                }}
              >
                Email Parts List
              </Button>
            ) : null}
            {(pathSegments[2] == "create" ||
              pathSegments[2] == "edit" ||
              pathSegments[2] == "start" ||
              pathSegments[2] == "complete") && (
              <Button
                disableElevation
                sx={{
                  textTransform: "capitalize",
                  fontSize: "9pt",
                  bgcolor: "primary",
                  color: "white",
                  padding: 0.75,
                  paddingX: 2.5,
                  minWidth: "auto",
                  borderRadius: "3px",
                  "&:hover": {
                    bgcolor: "primary",
                  },
                }}
                onClick={() => goToAddES()}
              >
                <Add fontSize="medium" />
              </Button>
            )}
          </Box>
        </Stack>
      </Stack>
      <Paper elevation={3}>
        <AppTableContainer>
          <AppTable stickyHeader>
            <AppTableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <AppTableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <AppTableCell key={header.id} align="center">
                      <AppTableSort
                        canSort={header.column.getCanSort()}
                        currentSort={header.column.getIsSorted()}
                        onSort={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </AppTableSort>
                    </AppTableCell>
                  ))}
                </AppTableRow>
              ))}
            </AppTableHead>
            <AppTableBody dataCount={dataCount}>
              {table.getRowModel().rows.map((row) => (
                <AppTableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <AppTableCell
                      key={cell.id}
                      align="center"
                      style={{
                        padding:
                          pathSegments[2] == "create" ||
                          pathSegments[2] == "edit" ||
                          pathSegments[2] == "start" ||
                          pathSegments[2] == "complete"
                            ? undefined
                            : "12px",
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </AppTableCell>
                  ))}
                </AppTableRow>
              ))}
            </AppTableBody>
          </AppTable>
        </AppTableContainer>
        <Box position="relative">
          <TablePagination
            sx={{ bgcolor: "white", borderTop: "1px solid #eee" }}
            component="div"
            count={dataCount}
            page={pageIndex}
            onPageChange={(e, page) => table.setPageIndex(page)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) =>
              table.setPageSize(Number(e.target.value))
            }
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Box>
      </Paper>
      <Outlet />
      <DeleteConfirmDialog
        openDeleteDialog={openESDeleteDialog}
        setOpenDeleteDialog={setOpenESDeleteDialog}
        setIsDeletionConfirmed={setIsDeletionConfirmed}
        type="task and part number"
      />
    </>
  );
};

export default React.memo(ESREquipmentServicesTable);
